const MAX_BYTES = 2 * 1024 * 1024; // 2MB
const MAX_DIM = 1600; // longest side in pixels (tune as you like)

function base(name?: string) {
    if (!name) return 'image';
    const i = name.lastIndexOf('.');
    return i > 0 ? name.slice(0, i) : name;
}

function fit(w: number, h: number, maxSide = MAX_DIM) {
    if (Math.max(w, h) <= maxSide) return { w, h };
    const scale = w >= h ? maxSide / w : maxSide / h;
    return { w: Math.round(w * scale), h: Math.round(h * scale) };
}

async function drawToCanvas(
    blob: Blob
): Promise<{ canvas: HTMLCanvasElement; w: number; h: number }> {
    // Try ImageBitmap with EXIF-aware decode
    try {
        if (
            'createImageBitmap' in window &&
            typeof createImageBitmap === 'function'
        ) {
            // Ask the browser to rotate pixels according to EXIF
            // If unsupported, this will throw and we’ll fall back to <img>
            const bmp: ImageBitmap = await createImageBitmap(blob, {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore - some TS libs don’t have the option typed yet
                imageOrientation: 'from-image',
            });

            const { w, h } = fit(bmp.width, bmp.height);
            const canvas = document.createElement('canvas');
            canvas.width = w;
            canvas.height = h;
            const ctx = canvas.getContext('2d');
            ctx?.drawImage(bmp, 0, 0, w, h);
            bmp.close?.();
            return { canvas, w, h };
        }
    } catch {
        // ignore and fallback
    }

    // Fallback: use <img>, which applies EXIF orientation in modern browsers
    const url = URL.createObjectURL(blob);
    try {
        const img = await new Promise<HTMLImageElement>((resolve, reject) => {
            const im = new Image();
            // optional: im.decoding = 'sync'; // to avoid extra async decode
            im.onload = () => resolve(im);
            im.onerror = reject;
            im.src = url;
        });

        const natW = img.naturalWidth || img.width;
        const natH = img.naturalHeight || img.height;
        const { w, h } = fit(natW, natH);

        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx?.drawImage(img, 0, 0, w, h);
        return { canvas, w, h };
    } finally {
        URL.revokeObjectURL(url);
    }
}

async function canvasToJpeg(
    canvas: HTMLCanvasElement,
    q: number
): Promise<Blob> {
    return new Promise((resolve, reject) =>
        canvas.toBlob(
            (b) => (b ? resolve(b) : reject(new Error('toBlob failed'))),
            'image/jpeg',
            q
        )
    ) as Promise<Blob>;
}

export async function prepareImageForUpload(file: File): Promise<File> {
    const isJpegPng = /^image\/(jpeg|png)$/i.test(file.type);
    const needsTranscode =
        /(heic|heif|webp)$/i.test(file.type) ||
        !isJpegPng ||
        file.size > MAX_BYTES;

    if (!needsTranscode) return file;

    const { canvas } = await drawToCanvas(file);

    // first pass quality target based on current size; clamp nicely
    const quality = Math.min(
        0.92,
        Math.max(0.6, MAX_BYTES / (file.size || MAX_BYTES))
    );
    let blob = await canvasToJpeg(canvas, quality);

    if (blob.size > MAX_BYTES) {
        const q2 = Math.max(0.5, (MAX_BYTES / blob.size) * quality);
        blob = await canvasToJpeg(canvas, q2);
    }
    if (blob.size > MAX_BYTES) {
        // last-resort strong squeeze if still a bit over
        blob = await canvasToJpeg(canvas, 0.5);
    }

    const out = new File([blob], `${base(file.name)}.jpg`, {
        type: 'image/jpeg',
        lastModified: Date.now(),
    });
    return out;
}
